import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Truck, Search, ChevronLeft, ChevronRight, FileStack, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, gbp, ukDate, errorMessage } from "../lib/api";
import { PageHeader, EmptyState } from "../components/Shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import SupplierMonthlyTable from "../components/suppliers/SupplierMonthlyTable";

function UnlinkedStatements({ restaurantId }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    let active = true;
    api.get("/statements", { params: { restaurant_id: restaurantId || undefined } })
      .then((r) => {
        if (!active) return;
        const flat = (r.data || []).filter((g) => !g.supplier_id).flatMap((g) =>
          g.years.flatMap((y) => y.months.flatMap((m) => m.statements.map((s) => ({ ...s, group_name: g.supplier_name }))))
        );
        setRows(flat);
      }).catch(() => {});
    return () => { active = false; };
  }, [restaurantId]);
  if (!rows.length) return null;
  return (
    <div className="mb-4 bg-amber-50 border border-amber-300 rounded-xl p-3" data-testid="unlinked-statements">
      <p className="text-sm font-semibold text-amber-900 flex items-center gap-1.5 mb-2"><AlertTriangle className="h-4 w-4" /> {rows.length} statement{rows.length === 1 ? "" : "s"} not linked to a supplier — open to assign</p>
      <ul className="divide-y divide-amber-200">
        {rows.map((s) => (
          <li key={s.id}>
            <Link to={`/statements/${s.id}`} className="flex items-center gap-3 px-2 py-2 hover:bg-amber-100/60 rounded" data-testid={`unlinked-statement-${s.id}`}>
              <FileStack className="h-4 w-4 text-amber-600 shrink-0" />
              <span className="min-w-0 flex-1 text-sm text-slate-800 truncate">{s.group_name}{s.statement_date ? ` · ${ukDate(s.statement_date)}` : ""}<span className="block text-xs text-slate-500 truncate">{s.original_filename}</span></span>
              {s.total_balance != null && <span className="font-mono text-sm text-slate-700">{gbp(s.total_balance)}</span>}
              <ChevronRight className="h-4 w-4 text-slate-400" />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

const EMPTY = { name: "", vat_number: "", telephone: "", email: "", address: "", account_number: "" };

export default function Suppliers() {
  const { restaurantId } = useAuth();
  const [data, setData] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/suppliers/monthly", { params: { year, restaurant_id: restaurantId || undefined } }).then((r) => setData(r.data)).catch((e) => toast.error(errorMessage(e)));
  useEffect(() => { load(); }, [restaurantId, year]); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/suppliers", form); toast.success("Supplier added"); setOpen(false); setForm(EMPTY); load(); }
    catch (err) { toast.error(errorMessage(err)); }
  };

  const years = data?.years || [year];
  const idx = years.indexOf(year);
  return (
    <div data-testid="suppliers-page">
      <PageHeader title="Suppliers" subtitle="Invoices and spend per month, by invoice date. Click a month to see its invoices, or the name to open the supplier."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button variant="outline" className="h-11" data-testid="add-supplier-button">Add supplier</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New supplier</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-3" data-testid="supplier-form">
                {[["name", "Name *"], ["vat_number", "VAT number"], ["account_number", "Your account number"], ["telephone", "Telephone"], ["email", "Email"], ["address", "Address"]].map(([k, l]) => (
                  <div key={k} className="space-y-1"><Label>{l}</Label><Input required={k === "name"} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`supplier-${k}-input`} /></div>
                ))}
                <Button type="submit" className="w-full bg-slate-900" data-testid="supplier-save-button">Save supplier</Button>
              </form>
            </DialogContent>
          </Dialog>
        } />
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search supplier…" className="pl-9 h-10" data-testid="supplier-search" />
        </div>
        <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white p-1" data-testid="year-picker">
          <button type="button" onClick={() => setYear(years[idx - 1])} disabled={idx <= 0} className="h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-slate-100 disabled:opacity-30" data-testid="year-prev"><ChevronLeft className="h-4 w-4" /></button>
          <span className="font-display font-bold text-base px-2 min-w-[3.5rem] text-center" data-testid="year-label">{year}</span>
          <button type="button" onClick={() => setYear(years[idx + 1])} disabled={idx < 0 || idx >= years.length - 1} className="h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-slate-100 disabled:opacity-30" data-testid="year-next"><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>
      {data === null ? <p className="text-sm text-slate-500">Loading…</p> : data.suppliers.length === 0 ? (
        <EmptyState icon={Truck} title="No suppliers yet" hint="Suppliers are created when you confirm your first invoice." testId="suppliers-empty" />
      ) : <><UnlinkedStatements restaurantId={restaurantId} /><SupplierMonthlyTable data={data} query={query} restaurantId={restaurantId} onChanged={load} /></>}
    </div>
  );
}
